/**
 * Clean string or numeric inputs.
 */
export const cleanValue = (v: unknown): string => {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v).trim();
  return "";
};

/**
 * Extract pathname from relative or absolute URL strings.
 */
export const getPathFromUrl = (url?: string): string => {
  if (!url) return "";
  const parts = url.split("?");
  return parts[0] || "";
};

/**
 * Extract clean numeric category ID from parent item ID or category URL parameters.
 */
export const extractCleanCategoryId = (
  parentItemId?: string,
  parentCategoryUrl?: string,
): string | null => {
  if (parentItemId) {
    return String(parentItemId).replace("label-", "");
  }
  if (parentCategoryUrl) {
    try {
      const urlObj = new URL(parentCategoryUrl, "http://localhost");
      const catId =
        urlObj.searchParams.get("category_id") || urlObj.searchParams.get("id");
      if (catId) return catId;
    } catch (e) {
      const match = parentCategoryUrl.match(/[?&](?:category_id|id)=([^&]+)/);
      if (match && match[1]) return match[1];
    }
  }
  return null;
};

/**
 * Determine if current filter context is associated with a navbar label category.
 */
export const computeIsNavbarLabel = (
  isNavbarLabelProp?: boolean,
  parentItemId?: string,
  parentCategoryUrl?: string,
): boolean => {
  if (isNavbarLabelProp !== undefined) return isNavbarLabelProp;
  if (parentItemId && String(parentItemId).startsWith("label-")) return true;
  if (parentCategoryUrl && parentCategoryUrl.includes("category_id="))
    return true;
  return false;
};

import { FilterField, FilterValueMap } from "../types";
import {
  compactSelection,
  findItemInfo,
  findParentOption,
} from "./tree-helpers";

export interface ComputeFilterUrlParams {
  filters: FilterValueMap;
  filterSchema: FilterField[];
  searchParams: URLSearchParams;
  parentCategoryUrl?: string;
  parentItemId?: string;
  isCollection?: boolean;
  computedIsNavbarLabel?: boolean;
  parentCleanId?: string | null;
  currentPathname?: string;
}

export interface ComputeFilterUrlResult {
  targetPath: string;
  newParams: string;
  fullUrl: string;
}

/**
 * Compute the target path and query parameters for URL synchronization.
 */
export const computeFilterUrl = ({
  filters,
  filterSchema,
  searchParams,
  parentCategoryUrl,
  parentItemId,
  isCollection,
  computedIsNavbarLabel,
  parentCleanId,
  currentPathname = typeof window !== "undefined"
    ? window.location.pathname
    : "",
}: ComputeFilterUrlParams): ComputeFilterUrlResult => {
  const params = new URLSearchParams(searchParams.toString());

  for (const field of filterSchema) {
    const key = field.id;
    const value = filters[key];

    if (Array.isArray(value)) {
      if (typeof value[0] === "number") {
        const paramValue = value.join("-");
        if (params.get(key) !== paramValue) {
          params.set(key, paramValue);
        }
      } else {
        const compacted = compactSelection(field, value);
        const cleaned = compacted
          .map((v: unknown) => cleanValue(v))
          .filter(Boolean);
        if (cleaned.length > 0) {
          const paramValue = cleaned.map(encodeURIComponent).join(",");
          params.set(key, paramValue);
        } else if (params.has(key)) {
          params.delete(key);
        }
      }
    } else if (value !== undefined && value !== "") {
      const cleanedVal = encodeURIComponent(cleanValue(value));
      params.set(key, cleanedVal);
    } else if (params.has(key)) {
      params.delete(key);
    }
  }

  let idValue = params.get("id");
  const originalCategoryId = searchParams.get("category_id");

  const idField = filterSchema.find((f) => f.id === "id");
  if (idField && idField.options && filters["id"]) {
    const selectedIds = Array.isArray(filters["id"])
      ? filters["id"].map(String)
      : [String(filters["id"])];
    const allTopLevelIds = idField.options.map((opt) => String(opt.id));
    const areAllSelected =
      allTopLevelIds.length > 0 &&
      allTopLevelIds.every((id: string) => selectedIds.includes(id));

    if (areAllSelected) {
      if (isCollection && parentItemId) {
        params.set("id", parentItemId);
        params.delete("category_id");
        idValue = parentItemId;
      } else if (computedIsNavbarLabel && parentCleanId) {
        params.set("category_id", parentCleanId);
        params.delete("id");
        idValue = null;
      } else if (originalCategoryId) {
        params.set("category_id", String(originalCategoryId));
        params.delete("id");
        idValue = null;
      }
    }
  }

  if (idValue && originalCategoryId && idValue === String(originalCategoryId)) {
    params.set("category_id", String(originalCategoryId));
    params.delete("id");
    idValue = null;
  }

  if (idValue) {
    if (params.has("category_id")) {
      params.delete("category_id");
    }
  } else {
    // Unchecked / empty selection logic
    if (computedIsNavbarLabel && parentCleanId) {
      if (params.get("category_id") !== parentCleanId || params.has("id")) {
        params.set("category_id", parentCleanId);
        params.delete("id");
      }
    } else if (originalCategoryId) {
      if (
        params.get("category_id") !== String(originalCategoryId) ||
        params.has("id")
      ) {
        params.set("category_id", String(originalCategoryId));
        params.delete("id");
      }
    } else if (parentCleanId && !isCollection) {
      if (params.get("id") !== parentCleanId || params.has("category_id")) {
        params.set("id", parentCleanId);
        params.delete("category_id");
      }
    }
  }

  if (params.has("parent_nav_collection_id")) {
    params.delete("parent_nav_collection_id");
  }

  let targetPath = currentPathname;

  if (idField && idField.options) {
    const selectedIds = idValue
      ? idValue
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    if (selectedIds.length === 1) {
      const targetId = selectedIds[0];
      if (targetId) {
        const optionInfo = findItemInfo(idField.options, targetId);
        if (optionInfo?.item?.url) {
          const path = getPathFromUrl(optionInfo.item.url);
          if (path) {
            targetPath = path;
          }
        } else if (parentCategoryUrl) {
          const path = getPathFromUrl(parentCategoryUrl);
          if (path) {
            targetPath = path;
          }
        }
      }
    } else if (selectedIds.length > 1) {
      const targetId = selectedIds[0];
      if (targetId) {
        const parentOption = findParentOption(idField.options, targetId);
        if (parentOption?.url) {
          const path = getPathFromUrl(parentOption.url);
          if (path) {
            targetPath = path;
          }
        } else if (parentCategoryUrl) {
          const path = getPathFromUrl(parentCategoryUrl);
          if (path) {
            targetPath = path;
          }
        }
      }
    } else {
      if (parentCategoryUrl) {
        const path = getPathFromUrl(parentCategoryUrl);
        if (path) {
          targetPath = path;
        }
      }
    }
  } else {
    if (parentCategoryUrl && !idValue) {
      const path = getPathFromUrl(parentCategoryUrl);
      if (path) {
        targetPath = path;
      }
    }
  }

  const newParams = params.toString();
  const fullUrl = `${targetPath}${newParams ? `?${newParams}` : ""}`;

  return { targetPath, newParams, fullUrl };
};
